// ProductDetail.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ProductGroupDetail.css';
import { productGroupsAPI, brandColorsAPI, productComboAPI, userEventAPI } from '../../utils/api';
import { getDisplayName, VariantCard, useImageLoader } from './ProductGroupDetailCommon';

const ProductDetail = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();

  // ── States ──
  const [productGroup, setProductGroup] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [combos, setCombos] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [brandColors, setBrandColors] = useState([]);
  const [viewingGallery, setViewingGallery] = useState(false);

  const getImageUrl = useImageLoader();

  // ── Load Product Group ──
  useEffect(() => {
    loadProductGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // ── Load Product Suggestions ──
  useEffect(() => {
    if (!productGroup) return;
    const loadSuggestions = async () => {
      try {
        const res = await productGroupsAPI.getProductSuggestions(productGroup.group_id);
        setSuggestions(res.data.data || []);
      } catch (err) {
        console.error('Failed to load product suggestions', err);
        setSuggestions([]);
      }
    };
    loadSuggestions();
  }, [productGroup]);

  const variantNames = useMemo(() => {
    if (!productGroup?.preview_variants) return [];
    return productGroup.preview_variants.map(v =>
      getDisplayName(v, productGroup.preview_variants) ||
      v.attribute ||
      v.size ||
      v.sku
    );
  }, [productGroup]);

  useEffect(() => {
    if (!variantNames || variantNames.length === 0) return;
    const loadBrandColors = async () => {
      try {
        const res = await brandColorsAPI.getByNames(variantNames);
        setBrandColors(res.data.data || []);
      } catch (err) {
        console.error('Failed to load brand colors', err);
        setBrandColors([]);
      }
    };
    loadBrandColors();
  }, [variantNames]);

  const normalize = (str) =>
    (str || '')
 .toLowerCase()
 .replace(/\s+/g, ' ')
 .trim();

  const colorMap = useMemo(() => {
    const map = new Map();
    (brandColors || []).forEach(c => {
      if (c.name) map.set(normalize(c.name), c);
      if (c.code) map.set(normalize(c.code), c);
    });
    return map;
  }, [brandColors]);

  const loadProductGroup = async () => {
    setIsLoading(true);
    try {
      const response = await productGroupsAPI.getProductGroupById(groupId);
      const group = response.data.data;
      setProductGroup(group);

      console.log(group);

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (group.group_id) {
        try {
          await userEventAPI.track({
            user_id: user?.id || null,
            product_group_id: group.group_id,
            event_type: 'view'
          });
        } catch (err) {
          console.error('Failed to track view event', err);
        }
      }

      if (group.group_id) {
        const comboResponse = await productComboAPI.getComboBasicByProductGroupId(group.group_id);
        setCombos(comboResponse.data.data || []);
      }

      const firstInStock = group.preview_variants.find(v => v.inventory_qty > 0);
      setSelectedVariant(firstInStock || group.preview_variants[0]);
    } catch (error) {
      console.error('Error loading product group:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Variant Handlers ──
  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    setQuantity(1);
    setViewingGallery(false);
  };

  const handleVariantImageClick = (variant) => {
    const variantImg = getImageUrl(variant.image_url);
    const imgIndex = galleryImages.findIndex(m => m.url === variantImg);

    if (imgIndex >= 0) {
      setActiveImageIndex(imgIndex);
    }
    handleVariantSelect(variant);
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) return;
    setAddingToCart(true);
    try {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const existingIndex = cart.findIndex(item => item.sku === selectedVariant.sku);
      if (existingIndex >= 0) {
        cart[existingIndex].quantity += quantity;
      } else {
        const colorKey = (selectedVariant.attribute || selectedVariant.size || '').toLowerCase().trim();
        const colorInfo = colorMap.get(colorKey);
        cart.push({
          sku: selectedVariant.sku,
          name: productGroup.title,
          variant: selectedVariant.attribute || selectedVariant.size,
          price: parseFloat(selectedVariant.retail_price),
          quantity,
          image_url: getImageUrl(selectedVariant.image_url),
          vendor_name: productGroup.vendor_name,
          category_name: productGroup.category_name,
          color_code: colorInfo?.code || null,
          color_name: colorInfo?.name || null,
          color_value: colorInfo?.value || null
        });
      }
      localStorage.setItem('cart', JSON.stringify(cart));
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      alert(`Added ${quantity} item(s) to cart!`);

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (productGroup.group_id) {
        try {
          await userEventAPI.track({
            user_id: user?.id || null,
            product_group_id: productGroup.group_id,
            variant_sku: selectedVariant.sku,
            event_type: 'add_to_cart',
            quantity
          });
        } catch (err) {
          console.error('Failed to track add_to_cart event', err);
        }
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add to cart. Please try again.');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleQuantityChange = (delta) => {
    const newQty = quantity + delta;
    if (newQty >= 1 && newQty <= 99) setQuantity(newQty);
  };

  // ── Format Description ──
  const formatDescription = (text) => {
    if (!text) return null;

    // Chuẩn hoá
    const normalized = text
     .replace(/&nbsp;/g, ' ')
     .replace(/&amp;/g, '&')
     .replace(/\r\n/g, '\n')
     .replace(/\n{3,}/g, '\n\n')
     .trim();

    // Tách theo dòng đôi hoặc các heading hay gặp
    const lines = normalized.split('\n').map(l => l.trim()).filter(Boolean);

    const elements = [];
    let currentList = [];
    let keyCounter = 0;

    const flushList = () => {
      if (currentList.length) {
        elements.push(
          <ul key={`list-${keyCounter++}`} className="desc-list">
            {currentList.map((item, idx) => (
              <li key={idx} className="desc-list-item">{item}</li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    const isHeading = (line) => /^(INCLUDES|FEATURES|LISTING DETAILS|SPECS|SPECIFICATIONS):?$/i.test(line);
    const isBullet = (line) => /^[-•*]\s+/.test(line) || /^[A-Z][a-zA-Z\s]+:/.test(line);
    const isKeyValue = (line) => /^[A-Z][A-Za-z\s/()]+:\s*.+/.test(line);

    lines.forEach((line) => {
      // 1. Heading
      if (isHeading(line)) {
        flushList();
        elements.push(
          <h4 key={`h-${keyCounter++}`} className="desc-heading">
            {line.replace(/:$/, '')}
          </h4>
        );
        return;
      }

      // 2. Bullet bắt đầu bằng - hoặc •
      if (/^[-•*]\s+/.test(line)) {
        currentList.push(line.replace(/^[-•*]\s+/, ''));
        return;
      }

      // 3. Key: Value trong LISTING DETAILS
      if (isKeyValue(line)) {
        flushList();
        const [key,...rest] = line.split(':');
        elements.push(
          <div key={`kv-${keyCounter++}`} className="desc-keyvalue">
            <span className="desc-key">{key.trim()}:</span>
            <span className="desc-value">{rest.join(':').trim()}</span>
          </div>
        );
        return;
      }

      // 4. Feature có dạng "Auto Quiet On/Off:..."
      if (/^[A-Z][^:]{3,}: /.test(line)) {
        flushList();
        const [label,...rest] = line.split(':');
        elements.push(
          <p key={`p-${keyCounter++}`} className="desc-feature">
            <strong className="desc-strong">{label.trim()}:</strong> {rest.join(':').trim()}
          </p>
        );
        return;
      }

      // 5. Đoạn thường
      flushList();
      elements.push(
        <p key={`p-${keyCounter++}`} className="desc-paragraph">{line}</p>
      );
    });

    flushList();
    return <div className="product-description-content">{elements}</div>;
  };

  const displayVariant = selectedVariant;

  const galleryImages = useMemo(() => {
    if (!productGroup) return [];
    const media = [];
    const seen = new Set();

    const addUnique = (url, type = 'image') => {
      if (url &&!seen.has(url)) {
        seen.add(url);
        media.push({ url, type });
      }
    };

    // Ưu tiên video trước
    if (productGroup?.hero_video) addUnique(getImageUrl(productGroup.hero_video), 'video');
    if (productGroup?.hero_image) addUnique(getImageUrl(productGroup.hero_image), 'image');

    productGroup.preview_variants?.forEach(v => {
      if (v.image_url) addUnique(getImageUrl(v.image_url), 'image');
      if (v.images?.length) {
        v.images.forEach(img => addUnique(getImageUrl(img.image_url), 'image'));
      }
    });

    return media;
  }, [productGroup, getImageUrl]);

  const heroMedia = useMemo(() => {
    if (viewingGallery) {
      return galleryImages[activeImageIndex] || { url: getImageUrl(productGroup?.hero_image), type: 'image' };
    }

    // Nếu user chưa click thumbnail thì ưu tiên hero_video
    if (!viewingGallery && productGroup?.hero_video) {
      return { url: getImageUrl(productGroup.hero_video), type: 'video' };
    }

    return selectedVariant?.image_url
     ? { url: getImageUrl(selectedVariant.image_url), type: 'image' }
      : galleryImages[0] || { url: getImageUrl(productGroup?.hero_image), type: 'image' };
  }, [viewingGallery, activeImageIndex, galleryImages, selectedVariant, getImageUrl, productGroup]);

  const fallbackImage = getImageUrl(productGroup?.hero_image);

  const variantDisplayNames = useMemo(() => {
    if (!productGroup) return {};
    const names = {};
    (productGroup.preview_variants || []).forEach(v => {
      names[v.id] = getDisplayName(v, productGroup.preview_variants);
    });
    return names;
  }, [productGroup]);

  const availableSizes = useMemo(() => {
    if (!productGroup) return [];
    const variants = productGroup.preview_variants || [];
    const sizes = [...new Set(variants.map(v => v.size).filter(Boolean))];
    return sizes.length >= 2 && variants.length > 10? sizes : [];
  }, [productGroup]);

  useEffect(() => {
    if (availableSizes.length > 0 &&!selectedSize) setSelectedSize(availableSizes[0]);
  }, [availableSizes, selectedSize]);

  useEffect(() => {
    if (!selectedSize ||!productGroup) return;
    const sizeVariants = productGroup.preview_variants.filter(v => v.size === selectedSize);
    const firstInStock = sizeVariants.find(v => v.inventory_qty > 0);
    setSelectedVariant(firstInStock || sizeVariants[0]);
  }, [selectedSize, productGroup]);

  const filteredVariants = useMemo(() => {
    if (!productGroup) return [];
    let variants = productGroup.preview_variants || [];
    if (selectedSize && availableSizes.length > 0) variants = variants.filter(v => v.size === selectedSize);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      variants = variants.filter(v => {
        const name = variantDisplayNames[v.id] || '';
        return name.toLowerCase().includes(q) ||
          (v.attribute || '').toLowerCase().includes(q) ||
          (v.size || '').toLowerCase().includes(q) ||
          (v.sku || '').toLowerCase().includes(q);
      });
    }
    return variants;
  }, [productGroup, searchTerm, selectedSize, availableSizes, variantDisplayNames]);

  const showSearch = productGroup && productGroup.preview_variants.length > 8;

  const formatPriceRange = (min, max) => {
    if (min == null || max == null) return 'N/A';
    const minVal = Number(min);
    const maxVal = Number(max);
    if (isNaN(minVal) || isNaN(maxVal)) return 'N/A';
    return minVal === maxVal? `$${minVal.toFixed(2)}` : `$${minVal.toFixed(2)} - $${maxVal.toFixed(2)}`;
  };

  useEffect(() => {
    if (!productGroup) return;
    (productGroup.preview_variants || []).forEach(v => {
      if (v.image_url) new Image().src = getImageUrl(v.image_url);
    });
  }, [productGroup, getImageUrl]);

  if (isLoading) return (
    <div className="product-group-detail loading">
      <div className="spinner"></div>
      <p>Loading product...</p>
    </div>
  );

  if (!productGroup) return (
    <div className="product-group-detail error">
      <h2>Product not found</h2>
      <button onClick={() => navigate('/shop')} className="btn-back">Back to Shop</button>
    </div>
  );

  const isOutOfStock = selectedVariant && selectedVariant.inventory_qty <= 0;

  return (
    <div className="product-group-detail">
      <div className="detail-container">
        <div className="breadcrumb">
          <span onClick={() => navigate('/shop')} className="breadcrumb-link">Shop</span>
          <span className="breadcrumb-separator">/</span>
          {productGroup.parent_category_name && <>
            <span onClick={() => navigate(`/shop?category=${productGroup.parent_category_slug}`)} className="breadcrumb-link">{productGroup.parent_category_name}</span>
            <span className="breadcrumb-separator">/</span>
          </>}
          {productGroup.category_name && <>
            <span onClick={() => navigate(`/shop?category=${productGroup.category_slug}`)} className="breadcrumb-link">{productGroup.category_name}</span>
            <span className="breadcrumb-separator">/</span>
          </>}
          <span className="breadcrumb-current">{productGroup.title}</span>
        </div>

        <div className="detail-content">
          <div className="detail-image-section" style={{ position: 'relative' }}>
            <div className="main-image">
              {heroMedia?.url? (
                heroMedia.type === 'video'? (
                  <video
                    key={heroMedia.url}
                    src={heroMedia.url}
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="hero-video"
                  />
                ) : (
                  <img src={heroMedia.url} alt={productGroup.title} />
                )
              ) : (
                <div className="no-image">No Media Available</div>
              )}
            </div>

            {combos.length > 0 && (
              <div className="combo-overlay">
                {combos.map(combo => (
                  <div
                    key={combo.id}
                    className="combo-overlay-card"
                    onClick={() => navigate(`/product-group/${combo.id}`)}
                  >
                    <div className="combo-overlay-image">
                      {combo.hero_image? <img src={getImageUrl(combo.hero_image)} alt={combo.title} /> : <div className="no-image">No Image</div>}
                    </div>
                    <div className="combo-overlay-info">
                      <p className="combo-overlay-title">{combo.title}</p>
                      <p className="combo-overlay-price">${parseFloat(combo.combo_price).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {galleryImages.length > 1 && (
              <div className="image-thumbnails">
                {galleryImages.map((media, idx) => (
                  <button
                    key={idx}
                    className={`thumbnail-btn ${activeImageIndex === idx? 'active' : ''}`}
                    onClick={() => {
                      setActiveImageIndex(idx);
                      setViewingGallery(true);
                    }}
                  >
                    {media.type === 'video'? (
                      <div className="video-thumb">
                        <video src={media.url} muted />
                        <span className="play-icon">▶</span>
                      </div>
                    ) : (
                      <img src={media.url} alt={`${productGroup.title} ${idx + 1}`} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="detail-info-section">
            {productGroup.vendor_name && <p className="detail-vendor">{productGroup.vendor_name}</p>}
            <h1 className="detail-title">{productGroup.title}</h1>
            {productGroup.category_name && <p className="detail-category">{productGroup.parent_category_name? `${productGroup.parent_category_name} › ` : ''}{productGroup.category_name}</p>}
            <div className="detail-price">
              ${selectedVariant? parseFloat(selectedVariant.retail_price).toFixed(2) : productGroup.min_price.toFixed(2)}
            </div>

            {productGroup.preview_variants.length > 1 && (
              <div className="variant-selector">
                <div className="variant-selector-header">
                  <h3>Select Option <span className="variant-count-badge">{filteredVariants.length}</span></h3>
                </div>

                {availableSizes.length > 0 && (
                  <div className="size-selector">
                    <h4 className="size-selector-label">Size</h4>
                    <div className="size-selector-options">
                      {availableSizes.map(size => (
                        <button
                          key={size}
                          className={`size-option ${selectedSize === size? 'active' : ''}`}
                          onClick={() => { setSelectedSize(size); setSearchTerm(''); }}
                        >{size}</button>
                      ))}
                    </div>
                  </div>
                )}

                {showSearch && (
                  <div className="variant-search">
                    <input type="text" placeholder="Search options..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    {searchTerm && <button className="variant-search-clear" onClick={() => setSearchTerm('')}>×</button>}
                  </div>
                )}

                <div>
                  {selectedVariant && <span className="selected-label">Selected: {variantDisplayNames[selectedVariant.id] || selectedVariant.attribute || 'Standard'}</span>}
                </div>
                <div className="variant-grid">
                  {filteredVariants.map(v => (
                    <VariantCard
                      key={`${v.id || v.sku}`}
                      variant={v}
                      isSelected={selectedVariant?.id === v.id}
                      onSelect={handleVariantSelect}
                      onImageClick={handleVariantImageClick}
                      getImageUrl={getImageUrl}
                      fallbackImage={fallbackImage}
                      displayName={variantDisplayNames[v.id] || v.attribute || 'Option'}
                      colorInfo={colorMap.get(normalize(v.attribute || v.size))}
                    />
                  ))}
                  {filteredVariants.length === 0 && <p className="variant-no-results">No options match "{searchTerm}"</p>}
                </div>
              </div>
            )}

            <div className="quantity-section">
              <h3>Quantity</h3>
              <div className="quantity-controls">
                <button className="qty-btn" onClick={() => handleQuantityChange(-1)} disabled={quantity <= 1}>−</button>
                <input type="number" value={quantity} onChange={e => {
                  const val = parseInt(e.target.value) || 1;
                  if (val >= 1 && val <= 99) setQuantity(val);
                }} min="1" max="99" />
                <button className="qty-btn" onClick={() => handleQuantityChange(1)} disabled={quantity >= 99}>+</button>
              </div>
            </div>

            {selectedVariant && (
              <div className="stock-status">
                {isOutOfStock? (
                  <span className="out-of-stock-text">Out of Stock</span>
                ) : selectedVariant.inventory_qty < 10? (
                  <span className="low-stock-text">Only {selectedVariant.inventory_qty} left in stock</span>
                ) : (
                  <span className="in-stock-text">In Stock</span>
                )}
              </div>
            )}

            <button className="btn-add-to-cart" onClick={handleAddToCart} disabled={!selectedVariant || isOutOfStock || addingToCart}>
              {addingToCart? 'Adding...' : isOutOfStock? 'Out of Stock' : 'Add to Cart'}
            </button>

            <div className="product-details">
              <h3>Product Details</h3>
              <ul className="details-meta">
                <li><strong>SKU:</strong> {selectedVariant?.sku}</li>
                {productGroup.brand_name && <li><strong>Brand:</strong> {productGroup.brand_name}</li>}
                {productGroup.category_name && <li><strong>Category:</strong> {productGroup.parent_category_name? `${productGroup.parent_category_name} › ` : ''}{productGroup.category_name}</li>}
                {selectedVariant?.size && <li><strong>Size:</strong> {selectedVariant.size}</li>}
                {productGroup.finish_type && <li><strong>Finish:</strong> {productGroup.finish_type}</li>}
                {productGroup.cure_time && <li><strong>Cure Time:</strong> {productGroup.cure_time}</li>}
              </ul>
              {productGroup.description && (
                <div className="product-description">
                  {formatDescription(productGroup.description)}
                </div>
              )}
            </div>
          </div>
        </div>

        {suggestions.length > 0 && (
          <div className="product-suggestions">
            <h3>You May Also Like</h3>
            <div className="suggestions-grid">
              {suggestions.map(item => (
                <div
                  key={item.product_id}
                  className="suggestion-card"
                  onClick={() => navigate(`/product-group/${item.product_id}`)}
                >
                  <div className="suggestion-image">
                    <img
                      src={getImageUrl(item.hero_image)}
                      alt={item.title}
                      onError={(e) => { e.currentTarget.src = getImageUrl(productGroup?.hero_image); }}
                    />
                  </div>
                  <div className="suggestion-info">
                    <p className="suggestion-title">{item.title}</p>
                    {item.vendor_name && <p className="suggestion-vendor">{item.vendor_name}</p>}
                    <p className="suggestion-price">
                      {formatPriceRange(item.min_price, item.max_price)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;